import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef } from 'react';
import Popup from '../../../../../components/common-components/pop-up/Popup';
import { Pencil } from 'lucide-react';
import { useStore } from '../../../../../hooks/useStore';
import { Form, Formik } from 'formik';
import FormPopupFooter from '../../../../../components/form-components/common-components/FormPopupFooter';
import { FormikTextField } from '../../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import * as Yup from 'yup';
import styles from './EditRowManually.module.css';
import { toast } from 'react-toastify';
import { hospitalRecordFieldIcons as HRI } from '../hospitalRecordFieldIcons';

type EditRowFormValues = {
  tagNumber: number | '';
  biomedicalDeviceDescription: string;
  brand: string;
  model: string;
  serialNumber: string;
  identificationNumber: string;
  location: string;
  responsiblePerson: string;
  note: string;
  tagColor: string;
  tagId: number | '';
};

const validationSchema = Yup.object({});

const EditRowManually = () => {
  const { popupStore, hospitalFilesStore } = useStore();

  const formikRef = useRef<any>(null);
  const originalColorRef = useRef<string>(hospitalFilesStore.selectedRecord?.color || 'green');
  const originalSequenceRef = useRef<number | ''>(
    hospitalFilesStore.selectedRecord?.tagSequence || ''
  );

  const getNextSequenceForColor = (color: string): number | '' => {
    const colorItem = hospitalFilesStore.tagColors?.find(
      x => x.color?.toLowerCase() === color?.toLowerCase()
    );
    if (colorItem && typeof colorItem.sequence === 'number') {
      return colorItem.sequence;
    }
    return '';
  };

  useEffect(() => {
    if (!hospitalFilesStore.tagColors?.length && hospitalFilesStore.selectedTag?.id) {
      hospitalFilesStore.getTagColors();
    }
  }, []);

  useEffect(() => {
    if (!formikRef.current) return;
    const currentColor: string = formikRef.current.values.tagColor;
    if (!currentColor) return;
    if (currentColor.toLowerCase() === (originalColorRef.current || '').toLowerCase()) {
      formikRef.current.setFieldValue('tagNumber', originalSequenceRef.current);
    } else {
      const next = getNextSequenceForColor(currentColor);
      formikRef.current.setFieldValue('tagNumber', next);
    }
  }, [hospitalFilesStore.tagColors]);

  const handleSubmit = async (values: any) => {
    try {
      if (!hospitalFilesStore.selectedRecord?.id) {
        toast.error('Kayıt ID bulunamadı');
        return;
      }

      await hospitalFilesStore.updateRecord(hospitalFilesStore.selectedRecord.id, {
        tagSequence: Number(values.tagNumber),
        biomedicalDeviceDescription: values.biomedicalDeviceDescription,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.identificationNumber,
        location: values.location,
        responsiblePerson: values.responsiblePerson,
        note: values.note,
        tagColor: values.tagColor,
        tagId: values.tagId,
      });

      hospitalFilesStore.setSelectedRecord(null);
      popupStore.setIsEditManuelRowOpen(false);
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme sırasında bir hata oluştu');
    }
  };

  const handleClose = () => {
    hospitalFilesStore.setSelectedRecord(null);
    popupStore.setIsEditManuelRowOpen(false);
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    return hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
  };

  const initialValues = {
    tagNumber: hospitalFilesStore.selectedRecord?.tagSequence || '',
    biomedicalDeviceDescription:
      hospitalFilesStore.selectedRecord?.biomedicalDeviceDescription || '',
    brand: hospitalFilesStore.selectedRecord?.brand || '',
    model: hospitalFilesStore.selectedRecord?.model || '',
    serialNumber: hospitalFilesStore.selectedRecord?.serialNumber || '',
    identificationNumber: hospitalFilesStore.selectedRecord?.identificationNumber || '',
    location: hospitalFilesStore.selectedRecord?.location || '',
    responsiblePerson: hospitalFilesStore.selectedRecord?.responsiblePerson || '',
    note: hospitalFilesStore.selectedRecord?.note || '',
    tagColor: hospitalFilesStore.selectedRecord?.color || 'green',
    tagId: hospitalFilesStore.selectedTag?.id || '',
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values }) => (
        <Popup
          isOpen={popupStore.isEditManuelRowOpen}
          onClose={handleClose}
          title="Veri Düzenleme"
          description="Kayıt bilgilerini güncelleyin"
          variant="success"
          icon={<Pencil size={22} strokeWidth={2} />}
          footer={() => (
            <FormPopupFooter
              onClose={handleClose}
              saveLabel="Güncelle"
              submittingLabel="Güncelleniyor..."
            />
          )}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="tagColor"
                  label="Etiket Rengi"
                  placeholder="Etiket rengi seçiniz"
                  startIcon={HRI.color}
                  options={[
                    { value: 'red', label: 'Kırmızı' },
                    { value: 'yellow', label: 'Sarı' },
                    { value: 'green', label: 'Yeşil' },
                  ]}
                  onChange={(value: string | number) => {
                    const v = (value as string) || originalColorRef.current;
                    setFieldValue('tagColor', v);
                    if (v.toLowerCase() === (originalColorRef.current || '').toLowerCase()) {
                      setFieldValue('tagNumber', originalSequenceRef.current);
                    } else {
                      const next = getNextSequenceForColor(v);
                      setFieldValue('tagNumber', next);
                    }
                  }}
                />
              </div>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="tagNumber"
                  label="Etiket No"
                  placeholder="Etiket no giriniz"
                  type="text"
                  startIcon={HRI.labelNo}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="biomedicalDeviceDescription"
                  label="Biyomedikal Cihaz Tanımı"
                  placeholder="Cihaz tanımını giriniz"
                  fieldToSearch="biomedicalDeviceDescription"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.deviceDefinition}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="brand"
                  label="Marka"
                  placeholder="Marka giriniz"
                  fieldToSearch="brand"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.brand}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="model"
                  label="Model"
                  placeholder="Model giriniz"
                  fieldToSearch="model"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.model}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="serialNumber"
                  label="Seri Numarası"
                  placeholder="Seri numarası giriniz"
                  startIcon={HRI.serialNumber}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="identificationNumber"
                  label="Künye Numarası"
                  placeholder="Künye numarası giriniz"
                  startIcon={HRI.tagNumber}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="location"
                  label="Bulunduğu Yer"
                  placeholder="Bulunduğu yeri giriniz"
                  fieldToSearch="location"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.location}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="responsiblePerson"
                  label="Zimmet Sahibi"
                  placeholder="Zimmet sahibini giriniz"
                  fieldToSearch="responsiblePerson"
                  onSuggestionSearch={handleSuggestionSearch}
                  startIcon={HRI.owner}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="note"
                  label="Not"
                  placeholder="Not giriniz"
                  startIcon={HRI.notes}
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditRowManually);
